import { Banner } from '@sunwebgroup/uds-ui/banner'
import { Button } from '@sunwebgroup/uds-ui/button'
import { Sheet } from '@sunwebgroup/uds-ui/sheet'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { useIntl } from '@/intl'
import { useBookingDetailsActions } from '@/scopes/account/booking-details/infrastructure/dependencies/dependencies'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { useBookingDetailsStore } from '../../infrastructure/useBookingDetailsStore'
import {
  getActiveParticipantExtraInfo,
  getIsEditableParticipant,
} from '../selectors'
import { buildParticipantCheckedLuggageDescriptions } from '../utils/flightLuggage'
import { formatParticipantFullName } from '../utils/participantName'
import styles from './ParticipantDetailsSheetPortal.module.css'

interface ParticipantDetailFieldProps {
  label: string
  value?: string
  values?: string[]
}

function ParticipantDetailField({ label, value, values }: ParticipantDetailFieldProps) {
  const items = (values ?? (value !== undefined ? [value] : []))
    .filter(item => item.trim().length > 0)

  if (items.length === 0) {
    return null
  }

  return (
    <div className={styles.fieldSection}>
      <Typography className={styles.fieldSectionTitle} as="div" variant="body-s-regular-tight">
        {label}
      </Typography>
      {/* eslint-disable-next-line react/no-array-index-key */}
      {items.map((item, index) => <Typography key={`${item}-${index}`} className={styles.fieldSectionValue} as="div">{item}</Typography>)}
    </div>
  )
}

export function ParticipantDetailsSheetPortal() {
  const intl = useIntl()
  const locale = useConfiguration('locale')
  const { participantEditOpened, participantEditSaveAcknowledged } = useBookingDetailsActions()
  const isSaved = useBookingDetailsStore(state => state.participantEdit.submitStatus === 'success')
  const participantExtraInfo = useBookingDetailsStore(state => getActiveParticipantExtraInfo(state, locale))
  const isEditableParticipant = useBookingDetailsStore(getIsEditableParticipant)
  const activeParticipantId = useBookingDetailsStore(state => state.activeParticipantId)
  const flightInformation = useBookingDetailsStore(state => state.flightInformation)
  const changeActiveParticipantId = useBookingDetailsStore(state => state.changeActiveParticipantId)
  const isOpen = !!activeParticipantId
  const checkedLuggageDescriptions = buildParticipantCheckedLuggageDescriptions(flightInformation, activeParticipantId, intl.formatMessage)

  function handleOpenChange(open: boolean) {
    if (!open) {
      changeActiveParticipantId('')
      participantEditSaveAcknowledged()
    }
  }

  function handleEditParticipantClick() {
    participantEditOpened(activeParticipantId)
  }

  const { titleKey, firstName, lastName } = participantExtraInfo.fullName

  return (
    <Sheet.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Sheet.Body side="right">
        <Sheet.Header
          closeLabel={intl.formatMessage({ id: 'close' })}
          titleLabel={intl.formatMessage({ id: 'common_participant' })}
        />
        <Sheet.Content className={styles.sheetContent}>
          {isSaved && (
            <Banner
              hasIcon
              color="success"
              size="M"
              className={styles.savedBanner}
              contentIsCentered
            >
              <Banner.TextsWrapper>
                <Banner.Text>{intl.formatMessage({ id: 'booking_details_participant_edit_success_message' })}</Banner.Text>
              </Banner.TextsWrapper>
            </Banner>
          )}
          <Typography className={styles.sheetTitle} as="div" variant="display-g-regular">
            {formatParticipantFullName(
              titleKey ? intl.formatMessage({ id: titleKey }) : null,
              firstName,
              lastName,
            )}
          </Typography>
          <ParticipantDetailField
            label={intl.formatMessage({ id: 'common_birthdate_label' })}
            value={participantExtraInfo.birthDate}
          />
          <ParticipantDetailField
            label={intl.formatMessage({ id: 'common_address' })}
            value={participantExtraInfo.address}
          />
          <ParticipantDetailField
            label={intl.formatMessage({ id: 'email_address' })}
            value={participantExtraInfo.email}
          />
          <ParticipantDetailField
            label={intl.formatMessage({ id: 'common_phone' })}
            values={participantExtraInfo.phoneNumbers}
          />
          <ParticipantDetailField
            label={intl.formatMessage({ id: 'common_emergency_phone' })}
            values={participantExtraInfo.emergencyPhoneNumbers}
          />
          {isEditableParticipant && (
            <div className={styles.editButton}>
              <Button
                onClick={handleEditParticipantClick}
                fullWidth
              >
                {intl.formatMessage({ id: 'common_edit' })}
              </Button>
            </div>
          )}
          {(participantExtraInfo.extras.length > 0 || checkedLuggageDescriptions.length > 0) && (
            <>
              <Typography className={styles.sectionTitle} as="div" variant="body-m-bold-tight">
                {intl.formatMessage({ id: 'common_extras' })}
              </Typography>
              {participantExtraInfo.extras.map(extra => (
                <ParticipantDetailField
                  key={extra.label}
                  label={extra.label}
                  values={extra.descriptions}
                />
              ))}
              {checkedLuggageDescriptions.length > 0 && (
                <ParticipantDetailField
                  label={intl.formatMessage({ id: 'booking_details_flight_luggage_title' })}
                  values={checkedLuggageDescriptions}
                />
              )}
            </>
          )}
        </Sheet.Content>
      </Sheet.Body>
    </Sheet.Root>
  )
}
